import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/axiosConfig";
import endpoints from "../api/endpoints";
import { blogImageSrc } from "../api/blogImage";
import Breadcrumb from "../components/Breadcrumb";

function SingleBlog() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentBlogs, setRecentBlogs] = useState([]);

  // Load single blog
  useEffect(() => {
    async function loadBlog() {
      try {
        const res = await api.get(`${endpoints.BLOGS}/${slug}`);
        setBlog(res.data.blog || null);
      } catch (err) {
        console.error("Error loading blog:", err);
      } finally {
        setLoading(false);
      }
    }
    loadBlog();
  }, [slug]);

  useEffect(() => {
    if (!blog) return undefined;

    const previousTitle = document.title;
    document.title = blog.meta_title || blog.title || "Blog";

    const upsertMeta = (name, content) => {
      if (!content) return () => {};
      let tag = document.querySelector(`meta[name="${name}"]`);
      const created = !tag;
      const previous = tag ? tag.getAttribute("content") : null;
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
      return () => {
        if (created) {
          tag.remove();
          return;
        }
        if (previous === null) tag.removeAttribute("content");
        else tag.setAttribute("content", previous);
      };
    };

    const restoreDescription = upsertMeta("description", blog.meta_description);
    const restoreKeywords = upsertMeta("keywords", blog.meta_keywords);

    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreKeywords();
    };
  }, [blog]);

  // Load recent blogs
  useEffect(() => {
    async function loadRecentBlogs() {
      try {
        const res = await api.get(endpoints.BLOGS);
        setRecentBlogs(res.data.blogs?.slice(0, 5) || []);
      } catch (err) {
        console.error("Error loading recent blogs:", err);
      }
    }
    loadRecentBlogs();
  }, []);

  if (loading) return <p className="text-center py-5">Loading blog...</p>;
  if (!blog) return <p className="text-center py-5">Blog not found.</p>;

  return (
    <>
      <Breadcrumb
        title={blog.title}
        path={
          <>
            <a href="/blog">Blog</a> &raquo; {blog.title}
          </>
        }
      />

      <section className="single-blog-section py-120">
        <div className="container">
          <div className="row">
            {/* MAIN BLOG CONTENT */}
            <div className="col-lg-8">
              <img
                src={blogImageSrc(blog.image)}
                alt={blog.title}
                className="single-blog-main-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = blogImageSrc();
                }}
              />

              <h2 className="single-blog-title">{blog.title}</h2>

              <p className="single-blog-meta">
                <span>📅 {new Date(blog.createdAt).toLocaleDateString()}</span>{" "}
                • <span>✍️ Admin</span>
              </p>

              <div
                className="single-blog-content"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </div>

            {/* SIDEBAR */}
            <div className="col-lg-4">
              <aside className="blog-sidebar">
                {/* RECENT POSTS ONLY */}
                <div className="sidebar-box">
                  <h4 className="sidebar-title">Recent Posts</h4>

                  <ul className="recent-post-list">
                    {recentBlogs.map((item) => (
                      <li key={item._id}>
                        <Link to={`/blog/${item.slug}`}>
                          <img
                            src={blogImageSrc(item.image)}
                            alt={item.title}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = blogImageSrc();
                            }}
                          />
                          <span>{item.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default SingleBlog;
